import { useState, useEffect } from 'react';

import { ShieldStarIcon } from '@c/icons';
import BaseModal from './BaseModal';

import { getReturnPolicy, PolicyDocument } from '@util/firestore/settings';

export const DEFAULT_RETURN_POLICY_HTML = `<h3 class="text-[1.5rem] font-bold">GearFocus Return Policy</h3>
<p>Your purchase is backed by clear, fair return terms.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">Default Return Policy</h3>
<p>Returns are accepted if the item does not match the listing description &mdash; including items damaged during shipping or misdescribed by the seller. If the item is not as described, the buyer is entitled to a full refund, including shipping costs.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">Return Deadlines</h3>
<p>Buyers must initiate a return request with the seller within 48 hours of receiving the item. Once the seller accepts the return, the buyer must ship the item back within 5 days.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">Packaging Requirements</h3>
<p>Returned items must be in the original condition, using original packaging, and include all parts and accessories.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">Fees for Voluntary Returns</h3>
<p>If a seller agrees to accept a return for reasons other than the item being misdescribed or damaged, the buyer will incur a 3.5% payment processing fee and a 5% restocking fee (both deducted from the refund), plus all return shipping costs.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">High-Value Items</h3>
<p>For items priced over $1,500, a 15% restocking fee may apply.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">No-Return Items</h3>
<p>Items listed as &ldquo;Poor&rdquo; or &ldquo;As-Is&rdquo; cannot be returned. These sales are final.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">14-Day Returns (Select Listings)</h3>
<p>Some approved sellers offer a 14-day, no-questions-asked return policy on eligible listings. Buyers cover return shipping and seller fees. Drones and items in &ldquo;Poor&rdquo; or &ldquo;As-Is&rdquo; condition are excluded. Check the listing for the seller&rsquo;s return policy before purchasing.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">Disputes</h3>
<p>If you and the seller can&rsquo;t resolve a return issue, contact us at <a class="text-brand-secondary hover:text-brand-primary" href="mailto:support@gearfocus.com">support@gearfocus.com</a> &mdash; we&rsquo;ll mediate.</p>
<p>&nbsp;</p>
<h3 class="text-[1.5rem] font-bold">Seller Return Options</h3>
<p>Seller return options may differ, including extended return windows. Check the Info tab on the seller&rsquo;s store page for their specific return policy before purchasing.</p>
<p>&nbsp;</p>
<p><a class="text-brand-secondary hover:text-brand-primary font-semibold" href="/policies/terms">Read the full Terms and Conditions &rarr;</a></p>`;

interface ReturnPolicyModalProps {
  isOpen: boolean;
  dismiss?: () => void;
  returnPolicy?: string;
  resolvedPolicy?: PolicyDocument | null;
}

const ReturnPolicyModal = ({
  isOpen,
  dismiss,
  returnPolicy,
  resolvedPolicy,
}: ReturnPolicyModalProps) => {
  const [customPolicy, setCustomPolicy] = useState<PolicyDocument | null>(
    resolvedPolicy ?? null,
  );

  useEffect(() => {
    if (resolvedPolicy || !returnPolicy) return;
    const fetchReturnPolicy = async () => {
      try {
        const policy = await getReturnPolicy(returnPolicy);
        if (policy) setCustomPolicy(policy as PolicyDocument);
      } catch (error) {
        console.error('Error fetching return policy:', error);
      }
    };
    fetchReturnPolicy();
  }, [returnPolicy, resolvedPolicy]);

  const ModalTitle = () => {
    return (
      <div className="flex items-center gap-[0.8rem] font-semibold text-brand-secondary">
        <ShieldStarIcon />
        Return Policy
      </div>
    );
  };

  return (
    <>
      <BaseModal isOpen={isOpen} title={<ModalTitle />} dismiss={dismiss} width="50rem">
        <div
          className="w-full"
          dangerouslySetInnerHTML={{ __html: customPolicy?.html || DEFAULT_RETURN_POLICY_HTML }}
        ></div>
      </BaseModal>
    </>
  );
};

export default ReturnPolicyModal;
